import { Injectable } from '@angular/core';
import { rethrowAsSapError } from 'src/app/lego-utils';
import { IPaginatedResult, ISapDataService, ISort } from 'src/app/lego-utils';
import { drop as _drop, take as _take } from 'lodash-es';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BOMItem } from 'src/app/models';
import { BOMItemAdapter } from 'src/app/models/domain/adapters';
import { BomItemUpdateService } from 'src/app/services/bom-item-update.service';
import { DesignService } from 'src/app/state/design.service';
import { MaterialSearchDataService } from './material-search-data.service';

export interface IBOMItemDeleteOptions {
  ecoNumber?: string;
}

@Injectable({
  providedIn: 'root',
})
export class BOMItemDataService implements ISapDataService<
  BOMItem,
  string,
  Partial<BOMItem>,
  IBOMItemDeleteOptions
> {
  public constructor(
    private readonly designService: DesignService,
    private readonly bomItemUpdateService: BomItemUpdateService,
    private readonly bomItemAdapter: BOMItemAdapter,
    private readonly materialSearchDataService: MaterialSearchDataService
  ) {}

  public getSingle(id: string): Observable<BOMItem> {
    return this.designService.designId$.pipe(
      take(1),
      switchMap((designId) => this.bomItemUpdateService.getBomItem(designId, id)),
      rethrowAsSapError(),
      map((response) => {
        return this.bomItemAdapter.adaptToDomain(response);
      })
    );
  }

  public getMultiple(): Observable<BOMItem[]> {
    return this.get();
  }

  public getPage(
    page: number,
    perPage: number,
    _sort?: Array<ISort<BOMItem>>
  ): Observable<IPaginatedResult<BOMItem>> {
    const skip = (page - 1) * perPage;

    return this.get().pipe(
      map((bomItems) => {
        return {
          currentPage: page,
          // The SAP service doesn't support $top and $skip, so we do pagination client side for now.
          data: _take(_drop(bomItems, skip), perPage),
          perPage,
          total: bomItems.length,
        };
      })
    );
  }

  public update(bomItemId: string, entity: Partial<BOMItem>): Observable<any> {
    return this.designService.designId$.pipe(
      take(1),
      switchMap((designId) =>
        this.bomItemUpdateService.updateBomItem(
          {
            NextChangeNumber: entity.engineeringChangeOrderNumber,
            Item: bomItemId,
            ItemMaterial: entity.component,
            Quantity: entity.weight,
            Unit: entity.unit,
            SortSequence: entity.sortString,
            ItemCategory: entity.itemCategory,
          },
          designId
        )
      ),
      rethrowAsSapError()
    );
  }

  public create(bomItem: Partial<BOMItem>): Observable<BOMItem> {
    return this.designService.designId$.pipe(
      take(1),
      switchMap((designId) =>
        this.bomItemUpdateService.createBomItem(
          {
            Alternative: '1',
            HeaderMaterial: designId,
            // the API ignores this but it's required
            Item: ' ',
            ItemCategory: bomItem.itemCategory,
            ItemMaterial: bomItem.component,
            NextChangeNumber: bomItem.engineeringChangeOrderNumber ?? ' ',
            Plant: '500',
            Quantity: bomItem.weight,
            SortSequence: bomItem.sortString ?? ' ',
            Unit: bomItem.unit,
            Usage: 'M',
          },
          designId
        )
      ),
      rethrowAsSapError(),
      map((response) => {
        return this.bomItemAdapter.adaptToDomain(response);
      })
    );
  }

  public delete(bomItemId: string, options?: IBOMItemDeleteOptions): Observable<void> {
    return this.designService.designId$.pipe(
      take(1),
      switchMap((designId) =>
        this.bomItemUpdateService.deleteBomItem(
          {
            Item: bomItemId,
            DeleteIndicator: true,
            NextChangeNumber: options?.ecoNumber,
          },
          designId
        )
      ),
      rethrowAsSapError()
    );
  }

  private get(): Observable<BOMItem[]> {
    return this.designService.designId$.pipe(
      take(1),
      switchMap((designId) => this.bomItemUpdateService.getBomItems(designId)),
      rethrowAsSapError(),
      map((response) => {
        return response.map((bomItem) => this.bomItemAdapter.adaptToDomain(bomItem));
      }),
      switchMap((bomItems) =>
        this.materialSearchDataService
          .getMaterialDescriptions(bomItems.map((x) => x.component))
          .pipe(
            map((materials) =>
              bomItems.map((item) => {
                const material = materials.get(item.component);

                return {
                  ...item,
                  componentDescription: material ?? '',
                };
              })
            )
          )
      )
    );
  }
}
